import api from '../utils/api';
import {
  ApiResponse, AuthResult, Booking, BookingRequestPayload, BookingResult, BookingStatus,
  CalendarDay, DayAvailability, GalleryImage, Membership, MembershipRequestPayload, MembershipResult, MembershipStatus,
  PaymentStatus, SiteContent, SlotAvailability, Tour,
} from './types';

function unwrap<T>(data: ApiResponse<T>): T {
  return data.data;
}

// ------------------------------------------------------------------ PUBLIC
export const publicApi = {
  async content(lang: string = 'it'): Promise<Record<string, string>> {
    const res = await api.get<ApiResponse<Record<string, string>>>('/public/content', { params: { lang } });
    return unwrap(res.data) || {};
  },
  async gallery(category?: string): Promise<GalleryImage[]> {
    const res = await api.get<ApiResponse<GalleryImage[]>>('/public/gallery', {
      params: category ? { category } : {},
    });
    return unwrap(res.data) || [];
  },
  async tours(): Promise<Tour[]> {
    const res = await api.get<ApiResponse<Tour[]>>('/public/tours');
    return unwrap(res.data) || [];
  },
  async tourBySlug(slug: string): Promise<Tour> {
    const res = await api.get<ApiResponse<Tour>>(`/public/tours/${slug}`);
    return unwrap(res.data);
  },
  async tourAvailability(tourId: number): Promise<SlotAvailability[]> {
    const res = await api.get<ApiResponse<SlotAvailability[]>>(`/public/tours/${tourId}/availability`);
    return unwrap(res.data) || [];
  },
  async upcomingSlots(): Promise<SlotAvailability[]> {
    const res = await api.get<ApiResponse<SlotAvailability[]>>('/public/slots/upcoming');
    return unwrap(res.data) || [];
  },
  async tourCalendar(tourId: number, from: string, to: string): Promise<DayAvailability[]> {
    const res = await api.get<ApiResponse<DayAvailability[]>>(`/public/tours/${tourId}/calendar`, {
      params: { from, to },
    });
    return unwrap(res.data) || [];
  },
  async publicCalendar(from: string, to: string): Promise<CalendarDay[]> {
    const res = await api.get<ApiResponse<CalendarDay[]>>('/public/calendar', { params: { from, to } });
    return unwrap(res.data) || [];
  },
  async createBooking(payload: BookingRequestPayload): Promise<BookingResult> {
    const res = await api.post<ApiResponse<BookingResult>>('/public/bookings', payload);
    return unwrap(res.data);
  },
  async confirmBooking(token: string): Promise<string> {
    const res = await api.get<ApiResponse<string>>('/public/bookings/confirm', { params: { token } });
    return unwrap(res.data);
  },
  async cancelBooking(token: string): Promise<string> {
    const res = await api.get<ApiResponse<string>>('/public/bookings/cancel', { params: { token } });
    return unwrap(res.data);
  },
  async createMembership(payload: MembershipRequestPayload): Promise<MembershipResult> {
    const res = await api.post<ApiResponse<MembershipResult>>('/public/memberships', payload);
    return unwrap(res.data);
  },
};

// -------------------------------------------------------------------- AUTH
export const authApi = {
  async login(email: string, password: string): Promise<AuthResult> {
    const res = await api.post<ApiResponse<AuthResult>>('/auth/public/login', { email, password });
    return unwrap(res.data);
  },
};

// ------------------------------------------------------------------- ADMIN
export const adminApi = {
  content: {
    list: async (): Promise<SiteContent[]> =>
      unwrap((await api.get<ApiResponse<SiteContent[]>>('/admin/content')).data) || [],
    upsert: async (body: Partial<SiteContent>): Promise<SiteContent> =>
      unwrap((await api.post<ApiResponse<SiteContent>>('/admin/content', body)).data),
    updateValue: async (id: number, value: string, valueEn?: string): Promise<SiteContent> =>
      unwrap((await api.put<ApiResponse<SiteContent>>(`/admin/content/${id}`, { value, valueEn })).data),
    uploadImage: async (key: string, file: File, label?: string, section?: string): Promise<SiteContent> => {
      const form = new FormData();
      form.append('file', file);
      form.append('key', key);
      if (label) form.append('label', label);
      if (section) form.append('section', section);
      const res = await api.post<ApiResponse<SiteContent>>('/admin/content/image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return unwrap(res.data);
    },
    remove: async (id: number): Promise<void> => {
      await api.delete(`/admin/content/${id}`);
    },
  },
  gallery: {
    list: async (): Promise<GalleryImage[]> =>
      unwrap((await api.get<ApiResponse<GalleryImage[]>>('/admin/gallery')).data) || [],
    upload: async (file: File, category?: string, title?: string, altText?: string): Promise<GalleryImage> => {
      const form = new FormData();
      form.append('file', file);
      if (category) form.append('category', category);
      if (title) form.append('title', title);
      if (altText) form.append('altText', altText);
      const res = await api.post<ApiResponse<GalleryImage>>('/admin/gallery/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return unwrap(res.data);
    },
    update: async (id: number, body: Partial<GalleryImage>): Promise<GalleryImage> =>
      unwrap((await api.put<ApiResponse<GalleryImage>>(`/admin/gallery/${id}`, body)).data),
    reorder: async (ids: number[]): Promise<void> => {
      await api.post('/admin/gallery/reorder', { ids });
    },
    remove: async (id: number): Promise<void> => {
      await api.delete(`/admin/gallery/${id}`);
    },
  },
  tours: {
    list: async (): Promise<Tour[]> =>
      unwrap((await api.get<ApiResponse<Tour[]>>('/admin/tours')).data) || [],
    get: async (id: number): Promise<Tour> =>
      unwrap((await api.get<ApiResponse<Tour>>(`/admin/tours/${id}`)).data),
    create: async (body: Partial<Tour>): Promise<Tour> =>
      unwrap((await api.post<ApiResponse<Tour>>('/admin/tours', body)).data),
    update: async (id: number, body: Partial<Tour>): Promise<Tour> =>
      unwrap((await api.put<ApiResponse<Tour>>(`/admin/tours/${id}`, body)).data),
    remove: async (id: number): Promise<void> => {
      await api.delete(`/admin/tours/${id}`);
    },
  },
  slots: {
    list: async (): Promise<SlotAvailability[]> =>
      unwrap((await api.get<ApiResponse<SlotAvailability[]>>('/admin/slots')).data) || [],
    byTour: async (tourId: number): Promise<SlotAvailability[]> =>
      unwrap((await api.get<ApiResponse<SlotAvailability[]>>(`/admin/slots/tour/${tourId}`)).data) || [],
    create: async (body: any): Promise<SlotAvailability> =>
      unwrap((await api.post<ApiResponse<SlotAvailability>>('/admin/slots', body)).data),
    update: async (id: number, body: any): Promise<SlotAvailability> =>
      unwrap((await api.put<ApiResponse<SlotAvailability>>(`/admin/slots/${id}`, body)).data),
    remove: async (id: number): Promise<void> => {
      await api.delete(`/admin/slots/${id}`);
    },
  },
  availability: {
    byTour: async (tourId: number, from: string, to: string): Promise<DayAvailability[]> =>
      unwrap((await api.get<ApiResponse<DayAvailability[]>>(`/admin/availability/tour/${tourId}`, {
        params: { from, to },
      })).data) || [],
    setRange: async (tourId: number, from: string, to: string, capacity: number): Promise<void> => {
      await api.post('/admin/availability/range', { tourId, from, to, capacity });
    },
    setDay: async (tourId: number, date: string, capacity: number): Promise<void> => {
      await api.put('/admin/availability/day', null, { params: { tourId, date, capacity } });
    },
    removeDay: async (tourId: number, date: string): Promise<void> => {
      await api.delete('/admin/availability/day', { params: { tourId, date } });
    },
  },
  media: {
    upload: async (file: File, folder = 'tours'): Promise<{ url: string; key: string }> => {
      const form = new FormData();
      form.append('file', file);
      form.append('folder', folder);
      const res = await api.post<ApiResponse<{ url: string; key: string }>>('/admin/media/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return unwrap(res.data);
    },
  },
  bookings: {
    bySlot: async (slotId: number): Promise<Booking[]> =>
      unwrap((await api.get<ApiResponse<Booking[]>>(`/admin/bookings/slot/${slotId}`)).data) || [],
    byTour: async (tourId: number): Promise<Booking[]> =>
      unwrap((await api.get<ApiResponse<Booking[]>>(`/admin/bookings/tour/${tourId}`)).data) || [],
    confirm: async (id: number): Promise<Booking> =>
      unwrap((await api.post<ApiResponse<Booking>>(`/admin/bookings/${id}/confirm`, {})).data),
    cancel: async (id: number): Promise<Booking> =>
      unwrap((await api.post<ApiResponse<Booking>>(`/admin/bookings/${id}/cancel`, {})).data),
    setPayment: async (id: number, status: PaymentStatus): Promise<Booking> =>
      unwrap((await api.put<ApiResponse<Booking>>(`/admin/bookings/${id}/payment`, null, { params: { status } })).data),
  },
  memberships: {
    list: async (): Promise<Membership[]> =>
      unwrap((await api.get<ApiResponse<Membership[]>>('/admin/memberships')).data) || [],
    setStatus: async (id: number, status: MembershipStatus): Promise<Membership> =>
      unwrap((await api.put<ApiResponse<Membership>>(`/admin/memberships/${id}/status`, null, { params: { status } })).data),
    remove: async (id: number): Promise<void> => {
      await api.delete(`/admin/memberships/${id}`);
    },
  },

  // Proposte libere di date (cliente ha scelto date senza vincolo di slot).
  proposals: {
    // Paginato server-side con filtro status + search testuale.
    page: async (params: { page?: number; size?: number; status?: BookingStatus; search?: string }): Promise<{
      items: Booking[]; total: number; page: number; size: number; totalPages: number;
    }> => {
      const res = await api.get<ApiResponse<{ items: Booking[]; total: number; page: number; size: number; totalPages: number }>>(
        '/admin/bookings/proposals',
        { params: {
            page: params.page ?? 0,
            size: params.size ?? 20,
            ...(params.status ? { status: params.status } : {}),
            ...(params.search ? { search: params.search } : {}),
          } }
      );
      return res.data.data;
    },
  },

  // Google Calendar integration (OAuth admin).
  googleCalendarStatus: async (): Promise<any> =>
    unwrap((await api.get<ApiResponse<any>>('/admin/google/status')).data),
  // Chiede al BE l'URL di consent Google (chiamata autenticata via Bearer),
  // poi il chiamante fa window.open(url) verso Google direttamente.
  googleCalendarAuthUrl: async (): Promise<string> => {
    const res = await api.get<ApiResponse<{ authUrl: string }>>('/admin/google/oauth/init');
    return res.data.data.authUrl;
  },
  googleCalendarDisconnect: async (): Promise<void> => {
    await api.delete('/admin/google/disconnect');
  },
};
